import { useEffect, useState } from "react";

/**
 * Used to determine if the document matches the media query string, as well as to monitor the document to detect
 * when it matches (or stops matching) that media query.
 *
 * @param queryList -> array of media queries. A media query written would like '(max-width: 912px)'. Similar to how we write it in css
 * @returns an array where each breakpoint status (whether it matches breakpoint or not) is recorded as boolean
 *             on the same index where the corresponding media query was written in queryList argument.
 */
export const useMatchMediaQuery = (queryList: string[]): boolean[] => {
  const [mediaMatch, setMediaMatch] = useState<boolean[]>([]);

  useEffect(() => {
    const handleChange = (event: MediaQueryListEvent, index?: number) => {
      if (typeof index === "number") {
        setMediaMatch((prevState) => {
          const newState = [...prevState];
          newState[index] = event.matches;
          return newState;
        });
      }
    };

    /** Get a list of current media query object for each query &
     * simultaneously save the current media query result for that query
     *  */
    const mediaMatchList = queryList.map((query) => {
      const media = matchMedia(query);
      setMediaMatch((list) => [...list, media.matches]);
      return media;
    });

    // Use the list of media query object retreived above & use it to add listener for each of them
    mediaMatchList.forEach((match, index) => {
      match.addEventListener("change", (event) => handleChange(event, index));
    });

    // Use the list of media query object retreived above & use it to remove listener for each of them
    return () => {
      mediaMatchList.forEach((match) => {
        match.removeEventListener("change", handleChange);
      });
    };
  }, []);

  return mediaMatch;
};
